import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAssetServiceBaseUrl } from "../../../../config/apiBase";
import ModulePage, { ModuleToolbar } from "../../../../shared-ui/ModulePage";
import { EmptyState, ErrorState, Skeleton } from "../../../../shared-ui/States";
import "../../../../shared-ui/crane-tokens.css";

const token = () => localStorage.getItem("zTaskAuthToken") || "";

function assetApiRoot() {
  return `${getAssetServiceBaseUrl()}/api`;
}

export default function StitchAssetsPage() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const t = token();
      if (!t) throw new Error("Authentication token not found.");
      const res = await fetch(`${assetApiRoot()}/assets/`, {
        headers: {
          Authorization: `Bearer ${t}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error("Failed to load assets.");
      const data = await res.json();
      const rows = Array.isArray(data) ? data : [];
      rows.sort((a, b) => Number(b.id) - Number(a.id));
      setAssets(rows);
    } catch (e) {
      setErr(e.message || "Failed to load assets");
      setAssets([]);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) =>
      Object.values(a).some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [assets, query]);

  return (
    <ModulePage
      crumbs={["Operations", "ZAsset", "Assets"]}
      title="Asset register"
      description="Fleet and equipment records from the asset service."
      actions={
        <>
          <button type="button" className="crane-btn" onClick={load} disabled={busy}>
            Refresh
          </button>
          <button type="button" className="crane-btn crane-btn--primary" onClick={() => navigate("/assets/new")}>
            + New asset
          </button>
        </>
      }
    >
      <ModuleToolbar>
        <input
          type="search"
          className="crane-input"
          placeholder="Search code, name, type, status…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter assets"
          style={{ maxWidth: 360 }}
        />
      </ModuleToolbar>

      {busy && <Skeleton rows={6} height={40} />}
      {!busy && err && <ErrorState title="Could not load assets" description={err} onRetry={load} />}
      {!busy && !err && filtered.length === 0 && (
        <EmptyState
          title={query ? "No matching assets" : "No assets yet"}
          description={query ? "Try another search." : "Register equipment to track lifecycle and maintenance."}
          action={
            !query ? (
              <button type="button" className="crane-btn crane-btn--primary" onClick={() => navigate("/assets/new")}>
                New asset
              </button>
            ) : null
          }
        />
      )}
      {!busy && !err && filtered.length > 0 && (
        <div className="crane-card" style={{ overflowX: "auto" }}>
          <table className="crane-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Reg / serial</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((asset) => (
                <tr key={asset.id}>
                  <td>
                    <Link to={`/assets/${asset.id}`}>{asset.asset_code || asset.id}</Link>
                  </td>
                  <td>{asset.name || "—"}</td>
                  <td>{asset.asset_type || "—"}</td>
                  <td>{asset.active === false ? "Inactive" : asset.active === true ? "Active" : asset.status || "—"}</td>
                  <td>
                    {[asset.registration_number, asset.serial_number].filter(Boolean).join(" · ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModulePage>
  );
}
